/** @jsxImportSource @emotion/react */

import Typography from '@mui/material/Typography'
import React, { FC, FocusEvent, useEffect, useState } from 'react'
import { SubmissionErrors } from 'final-form'
import { Form } from 'react-final-form'
import { Link } from 'react-router-dom'
import slugify from 'slugify'
import { ApolloError } from '@apollo/client'
import { USER_AVATAR_PATH_S3 } from 'constants/images'
import { findAllTeamMember_findAllTeamMember_items as TeamMember } from '__generatedTypes__/findAllTeamMember'
import { UpdateTeamMemberInput } from '__generatedTypes__/globalTypes'
import AvatarInput from 'components/AvatarInput'
import { getFileNameFromImagekitLink } from 'utils/getFileNameFromImagekitLink'
import { useImageUpload } from 'utils/saveImageS3'
import FormButtons from 'components/FormButtons'
import { cmsRoutes, useLink } from 'routes/cmsRoutes'
import { QUERY_PARAM, SUCCESS_STORIES_TAB, STUDY_GUIDE_TAB } from 'pagesNew/BlogPages'
import { InputField } from 'components/Input'
import { SlugInputField } from 'components/SlugInputField/SlugInputField'
import { validateRequired } from 'utils/validation'
import * as classes from './styles'

const BAD_SLUG_ERROR_MESSAGE = 'Team Member with this slug already created :('

export const handleServerErrors = (error: unknown) => {
  if (error && typeof error === 'object' && 'graphQLErrors' in error) {
    const existingSlugErrorIndex = (error.graphQLErrors as ApolloError['graphQLErrors']).findIndex(({ message }) =>
      [BAD_SLUG_ERROR_MESSAGE].includes(message)
    )

    if (existingSlugErrorIndex !== -1) {
      return { slug: 'Team Member with this slug already exists' }
    }
  }
}

enum Entities {
  CrashCourses = 'crashCourses',
  SuccessStories = 'successStories',
  StudyGuides = 'studyGuide',
  Strategies = 'strategy',
}

export type DeletionError = {
  entity: Entities
  error: string
}

interface IProps {
  onClose: () => void
  selectedTeamMember: TeamMember | null
  onTeamMemberSubmit: (teamMemberProps: UpdateTeamMemberInput) => SubmissionErrors | Promise<SubmissionErrors> | void
  onTeamMemberDelete?: (selectedTeamMemberId: string) => void
  removeLoading: boolean
  deletionError?: DeletionError | null
}

const ManageTeamMemberModal: FC<IProps> = ({
  onClose,
  selectedTeamMember,
  onTeamMemberDelete,
  onTeamMemberSubmit,
  removeLoading,
  deletionError,
}) => {
  const isEditMode = !!selectedTeamMember
  const [isSlugEdited, setIsSlugEdited] = useState(isEditMode)
  const [isImageChanged, setIsImageChanged] = useState<boolean>(false)
  const [imageError, setImageError] = useState<boolean>(false)
  const [selectedImage, setSelectedImage] = useState<File | null>()
  const imageUpload = useImageUpload()
  const { getLink } = useLink()

  useEffect(() => {
    ;(async () => {
      const link = selectedTeamMember?.getAvatar.photo
      if (link) {
        const filename = getFileNameFromImagekitLink(link)
        if (link) {
          const response = await fetch(link)
          const blob = await response.blob()
          const file = new File([blob], filename)
          if (file) {
            setSelectedImage(file)
          }
        }
      }
    })()
  }, [selectedTeamMember?.getAvatar?.photo])

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleSubmit = async (fieldValues: UpdateTeamMemberInput) => {
    if (selectedImage) {
      setImageError(false)
      const teamMemberUrl = isImageChanged ? await imageUpload(selectedImage, USER_AVATAR_PATH_S3) : null
      const teamMemberProps: UpdateTeamMemberInput = {
        slug: fieldValues.slug,
        degree: fieldValues.degree,
        fullName: fieldValues.fullName,
        position: fieldValues.position,
        university: fieldValues.university,
        authorPageDescription: fieldValues.authorPageDescription,
        authorPageHighlights: fieldValues.authorPageHighlights,
        authorPageCredentials: fieldValues.authorPageCredentials,
        aboutAuthorDescription: fieldValues.aboutAuthorDescription,
        videoInstructorDescription: fieldValues.videoInstructorDescription,
        avatar: teamMemberUrl ?? fieldValues.avatar,
      }
      const errors = await onTeamMemberSubmit(teamMemberProps)

      if (errors) {
        return errors
      }

      onClose()
    } else {
      setImageError(true)
    }
  }

  const onDeleteClick = async () => {
    if (onTeamMemberDelete && selectedTeamMember) {
      onTeamMemberDelete(selectedTeamMember?.id)
    }
  }

  const getLinkFromEntity = (entity: Entities) => {
    if (entity === Entities.CrashCourses) {
      return getLink(cmsRoutes.CRASH_COURSE_LIST)
    }

    if (entity === Entities.SuccessStories) {
      return getLink(cmsRoutes.BLOG_PAGES, undefined, new URLSearchParams({ [QUERY_PARAM]: SUCCESS_STORIES_TAB.id }))
    }

    if (entity === Entities.StudyGuides) {
      return getLink(cmsRoutes.BLOG_PAGES, undefined, new URLSearchParams({ [QUERY_PARAM]: STUDY_GUIDE_TAB.id }))
    }

    if (entity === Entities.Strategies) {
      return getLink(cmsRoutes.STRATEGIES)
    }

    return ''
  }

  const handleNameChange =
    (onChange: (name: keyof UpdateTeamMemberInput, value: string) => void) => (e: FocusEvent<HTMLInputElement>) => {
      if (isSlugEdited) {
        return
      }

      onChange('slug', slugify(e.target.value, { lower: true }))
    }

  const handleSlugBlur =
    (onChange: (name: keyof UpdateTeamMemberInput, value: string) => void) => (e: FocusEvent<HTMLInputElement>) => {
      onChange('slug', slugify(e.target.value, { lower: true }))
    }

  return (
    <div css={classes.modalContainerWrapper}>
      <div css={classes.modalContainer}>
        <Form
          onSubmit={handleSubmit}
          initialValues={selectedTeamMember}
          render={({ handleSubmit, form, submitting }) => {
            const slugInputProps = {
              name: 'slug',
              validate: validateRequired,
              label: 'Author Page: Slug',
              placeholder: 'Enter Author Page: Slug',
              fullWidth: true,
              onBlur: handleSlugBlur(form.change),
              onChange: () => setIsSlugEdited(true),
            }

            return (
              <form css={classes.modalForm} onSubmit={handleSubmit}>
                <Typography css={classes.modalTitle} variant="title2">
                  {isEditMode ? 'Edit Team Member' : 'Create Team Member'}
                </Typography>
                <AvatarInput
                  isEditMode
                  imageError={imageError}
                  setImageError={setImageError}
                  selectedImage={selectedImage}
                  setSelectedImage={(value) => {
                    setSelectedImage(value)
                    setIsImageChanged(true)
                  }}
                />
                <InputField
                  name="fullName"
                  validate={validateRequired}
                  label="Name"
                  placeholder="Enter Name"
                  onBlur={handleNameChange(form.change)}
                  fullWidth
                />
                <div css={classes.columns(3)}>
                  <InputField name="degree" label="Degree" placeholder="Enter Degree" fullWidth />
                  <InputField name="university" label="University" placeholder="Enter University" fullWidth />
                  <InputField name="position" label="Position" placeholder="Enter Position" fullWidth />
                </div>
                {isEditMode ? <SlugInputField {...slugInputProps} /> : <InputField {...slugInputProps} />}
                <div css={classes.columns(2)}>
                  {/* Author Page - description */}
                  <InputField
                    name="authorPageDescription"
                    label="Author Page: Description"
                    placeholder="Enter Author Page: Description"
                    fullWidth
                    multiline
                  />
                  {/* Author Page - highlights */}
                  <InputField
                    name="authorPageHighlights"
                    label="Author Page: Highlights"
                    placeholder="Enter Author Page: Highlights"
                    fullWidth
                    multiline
                  />
                </div>
                <div css={classes.columns(2)}>
                  {/* Blog Post - author second line / short info */}
                  <InputField
                    name="authorPageCredentials"
                    label="Blog Page: Author Credentials"
                    placeholder="Enter Blog Page: Author Credentials"
                    fullWidth
                  />
                  {/* Blog Post - about the author */}
                  <InputField
                    name="aboutAuthorDescription"
                    label="Blog Page: About the Author"
                    placeholder="Enter Blog Page: About the Author"
                    fullWidth
                    multiline
                  />
                </div>
                {/* Videos List - expert description */}
                <InputField
                  name="videoInstructorDescription"
                  label="Video Instructor Description"
                  placeholder="Enter Video Instructor Description"
                  fullWidth
                  multiline
                />
                <FormButtons
                  onDelete={onDeleteClick}
                  onClose={onClose}
                  removeLoading={removeLoading}
                  submitting={submitting}
                  isEditMode={isEditMode}
                />
                {deletionError && (
                  <div css={classes.deletingError}>
                    <Typography variant="body2" css={classes.deletingErrorText}>
                      {deletionError.error}
                      <br />
                      <Link
                        css={classes.deletingErrorLink}
                        to={getLinkFromEntity(deletionError.entity)}
                        target="_blank"
                      >
                        Check here
                      </Link>
                    </Typography>
                  </div>
                )}
              </form>
            )
          }}
        />
      </div>
    </div>
  )
}
export default ManageTeamMemberModal
